import { useEffect, useRef, useState } from "react";
import { InfoHint } from "../components/InfoHint";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { hasPlanFeature } from "../lib/planFeatures";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { toast } from "sonner";
import { FileBarChart, Send, Mail, AlertTriangle, CheckCircle2, FolderOpen, Star, BellRing, FileSpreadsheet, FileDown, BookOpen, Sparkles } from "lucide-react";

const METRIC_LABELS = {
  mant_ritardo: { titolo: "Manutenzioni in ritardo", path: ["manutenzioni", "in_ritardo"], kind: "manut" },
  mant_scadenza: { titolo: "Manutenzioni in scadenza", path: ["manutenzioni", "in_scadenza"], kind: "manut" },
  mant_nuove: { titolo: "Manutenzioni nuove / non assegnate", path: ["manutenzioni", "non_assegnate"], kind: "manut" },
  prev_attesa: { titolo: "Preventivi in attesa di risposta", path: ["preventivi", "in_attesa_risposta"], kind: "prev" },
  prev_scaduti: { titolo: "Preventivi scaduti senza risposta", path: ["preventivi", "scaduti_senza_risposta"], kind: "prev" },
  sinistri: { titolo: "Sinistri aperti", path: ["sinistri_aperti"], kind: "sinistri" },
  ras_scadute: { titolo: "Attività RAS scadute", path: ["attivita_programmate_ras", "scadute"], kind: "ras" },
  ras_in_scad: { titolo: "Attività RAS in scadenza", path: ["attivita_programmate_ras", "in_scadenza"], kind: "ras" },
};

function _pickList(dati, path) {
  let cur = dati || {};
  for (const p of path) cur = (cur || {})[p];
  return Array.isArray(cur) ? cur : [];
}

export default function Reports() {
  const { user } = useAuth();
  const showRas = hasPlanFeature(user, "ras");
  const [periodo, setPeriodo] = useState("settimanale");
  const [report, setReport] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [sending, setSending] = useState(false);
  const [checking, setChecking] = useState(false);
  const [inviandoManuali, setInviandoManuali] = useState(false);
  const [aiReport, setAiReport] = useState(null);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [aiPdfBusy, setAiPdfBusy] = useState(false);
  const [aiDetail, setAiDetail] = useState(null); // { condominio, metric, items }
  const aiHtmlRef = useRef(null);

  // Iter122ei · gestione errore esplicita per evitare "Caricamento..." infinito quando
  // /reports/preview restituisce 403 (piano non abilitato in produzione pre-redeploy) o 500.
  const load = (p) => {
    setLoadError(null);
    return api.get("/reports/preview", { params: { periodo: p } })
      .then((r) => setReport(r.data))
      .catch((e) => setLoadError(e.response?.data?.detail || e.message || "Impossibile caricare il report"));
  };
  useEffect(() => { load(periodo); /* eslint-disable-next-line */ }, [periodo]);
  useEffect(() => { api.get("/ai/report/ultimo").then((r) => setAiReport(r.data.report)).catch(() => {}); }, []);

  const generaAi = async () => {
    setAiBusy(true);
    try {
      const { data } = await api.post("/ai/report/run");
      setAiReport(data.report); setAiOpen(true);
      toast.success("Report AI generato e inviato allo Studio");
    } catch { toast.error("Generazione del report AI non riuscita, riprova"); }
    finally { setAiBusy(false); }
  };

  // Iter122fw · Export PDF report AI
  const scaricaAiPdf = async () => {
    if (!aiReport?.id) return;
    setAiPdfBusy(true);
    try {
      const res = await api.get(`/ai/report/${aiReport.id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      const data = aiReport?.dati?.data_odierna || new Date(aiReport.created_at).toLocaleDateString("it-IT").replace(/\//g, "-");
      a.href = url; a.download = `Report_AI_${data}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Scaricamento PDF non riuscito"); }
    finally { setAiPdfBusy(false); }
  };

  // Iter122fw · celle cliccabili nella tabella riepilogo per condominio.
  // La tabella è dentro `dangerouslySetInnerHTML` quindi usiamo event-delegation
  // sul contenitore ref, leggendo data-cond/data-metric.
  useEffect(() => {
    const root = aiHtmlRef.current;
    if (!root || !aiReport?.dati) return;
    const handler = (ev) => {
      const td = ev.target.closest && ev.target.closest("td[data-cond][data-metric]");
      if (!td || !root.contains(td)) return;
      const cond = td.getAttribute("data-cond");
      const metric = td.getAttribute("data-metric");
      const meta = METRIC_LABELS[metric];
      if (!meta) return;
      const all = _pickList(aiReport.dati, meta.path);
      const items = all.filter((it) => {
        const c = it.condominio || (meta.kind === "ras" ? it.condominio : "");
        return c === cond;
      });
      if (items.length === 0) return;
      setAiDetail({ condominio: cond, metric, titolo: meta.titolo, kind: meta.kind, items });
    };
    root.addEventListener("click", handler);
    return () => root.removeEventListener("click", handler);
  }, [aiReport, aiOpen]);

  const download = async (path, filename) => {
    try {
      const res = await api.get(path, { responseType: "blob", params: { periodo } });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = filename; document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Download non riuscito"); }
  };

  const inviaManuali = async () => {
    setInviandoManuali(true);
    try {
      const { data } = await api.post("/manuali/invia");
      toast.success(`Manuali inviati a ${data.destinatario}`);
    } catch { toast.error("Invio manuali non riuscito"); }
    finally { setInviandoManuali(false); }
  };

  const send = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/reports/send", null, { params: { periodo } });
      toast.success(`Report inviato a ${data.destinatario}`);
    } catch { toast.error("Errore nell'invio del report"); }
    finally { setSending(false); }
  };

  const checkAlerts = async () => {
    setChecking(true);
    try {
      const { data } = await api.post("/alerts/run");
      if (data.solleciti_inviati > 0) {
        toast.success(`${data.solleciti_inviati} sollecito/i inviati (${data.in_ritardo} in ritardo)`);
      } else {
        toast.info(`Nessun nuovo sollecito (${data.in_ritardo} in ritardo, già notificate)`);
      }
      load(periodo);
    } catch { toast.error("Errore nel controllo alert"); }
    finally { setChecking(false); }
  };

  if (loadError) {
    return (
      <div className="max-w-5xl mx-auto" data-testid="reports-error">
        <Card className="p-6 border-2 border-red-200 bg-red-50">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-6 h-6 text-red-700 shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="font-bold text-red-900 text-lg mb-1">Report non disponibile</div>
              <p className="text-sm text-red-800 mb-3">{loadError}</p>
              <Button onClick={() => load(periodo)} data-testid="reports-retry"
                      className="bg-red-700 hover:bg-red-800 text-white">Riprova</Button>
            </div>
          </div>
        </Card>
      </div>
    );
  }
  if (!report) return <div className="text-lg" data-testid="reports-loading">Caricamento...</div>;

  return (
    <div className="max-w-5xl">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
        <div className="flex items-center gap-2">
          <h1 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight flex items-center gap-3">
            <FileBarChart className="w-9 h-9 text-brand" /> Report periodico
          </h1>
          <InfoHint section="report" />
        </div>
        <div className="flex gap-2">
          {["settimanale", "mensile"].map((p) => (
            <Button key={p} data-testid={`periodo-${p}`} onClick={() => setPeriodo(p)}
              variant={periodo === p ? "default" : "outline"}
              className={`h-12 px-5 text-base font-bold capitalize ${periodo === p ? "bg-brand hover:bg-brand-dark" : ""}`}>
              {p}
            </Button>
          ))}
        </div>
      </div>

      <Card className="p-5 border-2 mb-4 flex flex-col sm:flex-row items-center text-center sm:text-left gap-4 bg-[#FBEEF3]/40">
        <div className="flex items-center gap-3 min-w-0">
          <Mail className="w-7 h-7 shrink-0 text-brand" />
          <div className="min-w-0">
            <div className="font-semibold text-lg break-words">Invio a: {report.destinatario}</div>
            <div className="text-base text-slate-600">Riepilogo {periodo} di attività aperte, chiuse e valutazioni</div>
          </div>
        </div>
        <Button data-testid="send-report-button" onClick={send} disabled={sending} className="h-12 px-6 text-base font-bold bg-green-700 hover:bg-green-800 w-full sm:w-auto sm:ml-auto shrink-0">
          <Send className="w-5 h-5 mr-2" /> {sending ? "Invio..." : "Invia ora"}
        </Button>
      </Card>

      <Card className="p-5 border-2 mb-4 bg-gradient-to-r from-[#FBEEF3] to-white border-[#C98AA0]" data-testid="ai-report-card">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <div className="rounded-xl p-2.5 bg-brand text-[#D4AF5A] shrink-0"><Sparkles className="w-6 h-6" /></div>
            <div className="min-w-0">
              <div className="font-semibold text-lg">Report giornaliero AI</div>
              <div className="text-base text-slate-600">
                Sintesi ragionata di attività, scadenze e priorità del giorno — inviata via email allo Studio ogni mattina alle 07:30
              </div>
            </div>
          </div>
          <Button data-testid="ai-report-run" onClick={generaAi} disabled={aiBusy}
            className="h-12 px-6 text-base font-bold bg-brand hover:bg-brand-dark w-full sm:w-auto sm:ml-auto shrink-0">
            <Sparkles className="w-5 h-5 mr-2" /> {aiBusy ? "Generazione…" : "Genera ora"}
          </Button>
        </div>
        {aiReport && (
          <div className="mt-4 pt-4 border-t border-[#C98AA0]/40">
            <div className="flex flex-wrap items-center gap-3">
              <button data-testid="ai-report-toggle" onClick={() => setAiOpen((o) => !o)}
                className="text-brand font-bold text-sm hover:underline">
                {aiOpen ? "Nascondi" : "Mostra"} ultimo report ({new Date(aiReport.created_at).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })})
              </button>
              <Button data-testid="ai-report-pdf" onClick={scaricaAiPdf} disabled={aiPdfBusy}
                variant="outline" size="sm" className="ml-auto h-9 text-brand border-brand/40">
                <FileDown className="w-4 h-4 mr-1" /> {aiPdfBusy ? "PDF…" : "Scarica PDF"}
              </Button>
            </div>
            {aiOpen && (
              aiReport.testo_html ? (
                <div ref={aiHtmlRef} data-testid="ai-report-html"
                     className="mt-3 bg-white rounded-lg border border-slate-200 overflow-hidden"
                     dangerouslySetInnerHTML={{ __html: aiReport.testo_html }} />
              ) : (
                <div data-testid="ai-report-testo" className="mt-3 p-4 bg-white rounded-lg border border-slate-200 text-sm text-slate-800 whitespace-pre-wrap leading-relaxed">
                  {aiReport.testo}
                </div>
              )
            )}
            {aiOpen && (
              <div className="text-xs text-slate-500 mt-2">
                Suggerimento: tocca una cifra colorata nella tabella per aprire l'elenco dettagliato.
              </div>
            )}
          </div>
        )}
      </Card>

      {/* Iter122fw · Dettaglio metrica per condominio */}
      <Dialog open={!!aiDetail} onOpenChange={(o) => !o && setAiDetail(null)}>
        <DialogContent className="w-[95vw] max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="ai-report-detail">
          <DialogHeader>
            <DialogTitle className="text-lg flex flex-wrap items-center gap-2">
              <span className="text-brand">{aiDetail?.condominio}</span>
              <span className="text-slate-400">·</span>
              <span className="text-slate-700">{aiDetail?.titolo}</span>
              {aiDetail && <span className="text-xs text-slate-500 ml-auto">{aiDetail.items.length} voc{aiDetail.items.length === 1 ? "e" : "i"}</span>}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            {aiDetail?.items.map((it, i) => (
              <AiDetailRow key={i} item={it} kind={aiDetail.kind} />
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <Card className="p-5 border-2 mb-4 flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <FileDown className="w-7 h-7 text-brand" />
          <div>
            <div className="font-semibold text-lg">Esporta il report {periodo}</div>
            <div className="text-base text-slate-600">Scarica riepilogo ed elenco dettagliato delle manutenzioni</div>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button data-testid="export-excel-button" variant="outline" className="h-12 px-5 text-base font-bold border-green-300 text-green-800"
            onClick={() => download("/reports/export/excel", `report_${periodo}.xlsx`)}>
            <FileSpreadsheet className="w-5 h-5 mr-2" /> Excel
          </Button>
          <Button data-testid="export-pdf-button" variant="outline" className="h-12 px-5 text-base font-bold border-[#C98AA0] text-brand"
            onClick={() => download("/reports/export/pdf", `report_${periodo}.pdf`)}>
            <FileDown className="w-5 h-5 mr-2" /> PDF
          </Button>
        </div>
      </Card>

      <Card className="p-5 border-2 mb-4 flex flex-wrap items-center justify-between gap-4 bg-slate-50">
        <div className="flex items-center gap-3">
          <BookOpen className="w-7 h-7 text-slate-600" />
          <div>
            <div className="font-semibold text-lg">Manuali d'uso dell'app</div>
            <div className="text-base text-slate-600">Invia via email allo Studio i manuali operativi (Studio, Fornitori, Condòmini)</div>
          </div>
        </div>
        <Button data-testid="invia-manuali-button" onClick={inviaManuali} disabled={inviandoManuali} className="h-12 px-6 text-base font-bold bg-slate-700 hover:bg-slate-800">
          <BookOpen className="w-5 h-5 mr-2" /> {inviandoManuali ? "Invio..." : "Invia manuali"}
        </Button>
      </Card>

      <Card className="p-5 border-2 mb-4 flex flex-wrap items-center justify-between gap-4 bg-amber-50/50 border-amber-200">
        <div className="flex items-center gap-3">
          <BellRing className="w-7 h-7 text-amber-600" />
          <div>
            <div className="font-semibold text-lg">Controllo alert ritardi</div>
            <div className="text-base text-slate-600">Invia un sollecito allo Studio per le manutenzioni ferme da oltre 3 giorni (automatico ogni mattina alle 08:00)</div>
          </div>
        </div>
        <Button data-testid="check-alerts-button" onClick={checkAlerts} disabled={checking} className="h-12 px-6 text-base font-bold bg-amber-600 hover:bg-amber-700">
          <BellRing className="w-5 h-5 mr-2" /> {checking ? "Controllo..." : "Controlla alert ora"}
        </Button>
      </Card>

      <div className="grid sm:grid-cols-5 gap-4 mb-4">
        <Mini icon={FolderOpen} label="Nuove" value={report.totale_nuove} color="text-brand bg-blue-100" />
        <Mini icon={CheckCircle2} label="Chiuse" value={report.chiuse.length} color="text-green-700 bg-green-100" />
        <Mini icon={BellRing} label="In scadenza" value={(report.in_scadenza || []).length} color="text-amber-700 bg-amber-100" />
        <Mini icon={AlertTriangle} label="Scadute" value={report.in_ritardo.length} color="text-red-700 bg-red-100" />
        <Mini icon={Star} label="Valutazioni" value={report.valutazioni.length} color="text-amber-700 bg-amber-100" />
      </div>

      <AperteSection rows={report.aperte} />
      {/* Iter122ey · sezioni RAS visibili solo con piano Full/Pro+ (Trial/Light le nascondono) */}
      {showRas && (
        <>
          <AttivitaSection title="Attività programmate SCADUTE (RAS)" rows={report.attivita_scadute || []} alert empty="Nessuna attività programmata scaduta" testid="attivita-scadute" />
          <AttivitaSection title="Attività programmate in scadenza (RAS)" rows={report.attivita_in_scadenza || []} empty="Nessuna attività programmata in scadenza" testid="attivita-in-scadenza" />
        </>
      )}
      <Section title="Attività chiuse" rows={report.chiuse} cols={["numero", "condominio", "titolo", "fornitore"]} empty="Nessuna attività chiusa nel periodo" />
    </div>
  );
}

function AttivitaSection({ title, rows, empty, alert, testid }) {
  return (
    <Card className={`p-5 border-2 mb-5 ${alert && rows.length ? "border-red-300" : ""}`} data-testid={testid}>
      <h3 className="font-display text-xl font-bold mb-1">{title} ({rows.length})</h3>
      <p className="text-sm text-slate-500 mb-3">Messe a terra, verifiche ascensori, letture contabilizzatori e altri documenti del fascicolo RAS.</p>
      {rows.length === 0 ? (
        <div className="text-slate-500 text-base">{empty}</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-base">
            <thead>
              <tr className="text-left text-sm font-semibold text-slate-500 border-b border-slate-200">
                <th className="py-2 pr-4">Documento / attività</th><th className="py-2 pr-4">Condominio</th>
                <th className="py-2 pr-4">Fornitore incaricato</th><th className="py-2 pr-4">Scadenza</th>
                <th className="py-2 pr-4">Ricorrente</th><th className="py-2 pr-4">Solleciti</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} className="border-b border-slate-100">
                  <td className="py-2 pr-4 align-top">{r.documento}</td>
                  <td className="py-2 pr-4 align-top">{r.condominio}</td>
                  <td className="py-2 pr-4 align-top">{r.fornitore}</td>
                  <td className="py-2 pr-4 align-top">{r.scadenza}{r.giorni >= 0 ? ` (tra ${r.giorni} gg)` : ` (${Math.abs(r.giorni)} gg fa)`}</td>
                  <td className="py-2 pr-4 align-top">{r.ricorrente ? "Sì" : "No"}</td>
                  <td className="py-2 pr-4 align-top">{r.solleciti || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

function StatoBadge({ row }) {
  const cfg = row.in_ritardo
    ? { label: "Scaduta", cls: "bg-red-100 text-red-700" }
    : row.in_scadenza
    ? { label: "In scadenza", cls: "bg-amber-100 text-amber-700" }
    : { label: row.stato || "In corso", cls: "bg-slate-100 text-slate-600" };
  return <span className={`inline-block rounded-full px-2.5 py-0.5 text-sm font-semibold ${cfg.cls}`}>{cfg.label}</span>;
}

function AperteSection({ rows }) {
  return (
    <Card className="p-5 border-2 mb-5">
      <h3 className="font-display text-xl font-bold mb-1">Attività aperte ({rows.length})</h3>
      <p className="text-sm text-slate-500 mb-3">Manutenzioni in corso, con lo stato di scadenza evidenziato per ciascuna.</p>
      {rows.length === 0 ? (
        <div className="text-slate-500 text-base">Nessuna attività aperta</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-base">
            <thead>
              <tr className="text-left text-sm font-semibold text-slate-500 border-b border-slate-200">
                <th className="py-2 pr-4">N.</th><th className="py-2 pr-4">Condominio</th>
                <th className="py-2 pr-4">Titolo</th><th className="py-2 pr-4">Priorità</th>
                <th className="py-2 pr-4">Scadenza</th><th className="py-2 pr-4">Stato</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} className="border-b border-slate-100" data-testid={`aperta-row-${i}`}>
                  <td className="py-2 pr-4 align-top">{r.numero ?? "—"}</td>
                  <td className="py-2 pr-4 align-top">{r.condominio ?? "—"}</td>
                  <td className="py-2 pr-4 align-top">{r.titolo ?? "—"}</td>
                  <td className="py-2 pr-4 align-top">{r.priorita ?? "—"}</td>
                  <td className="py-2 pr-4 align-top">{r.scadenza ?? "—"}</td>
                  <td className="py-2 pr-4 align-top"><StatoBadge row={r} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

function AiDetailRow({ item, kind }) {
  if (kind === "manut") {
    return (
      <div className="p-3 border border-slate-200 rounded-lg bg-white">
        <div className="flex flex-wrap items-baseline gap-2">
          <span className="text-xs font-mono text-slate-500">{item.numero}</span>
          <span className="font-semibold text-slate-800">{item.titolo || "—"}</span>
          {item.priorita && <span className="ml-auto text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-700">{item.priorita}</span>}
        </div>
        <div className="text-xs text-slate-500 mt-1">Fornitore: <span className="text-slate-700">{item.fornitore || "—"}</span> · Stato: {item.stato || "—"}</div>
      </div>
    );
  }
  if (kind === "prev") {
    return (
      <div className="p-3 border border-slate-200 rounded-lg bg-white">
        <div className="flex flex-wrap items-baseline gap-2">
          <span className="text-xs font-mono text-slate-500">{item.numero}</span>
          <span className="font-semibold text-slate-800">{item.descrizione || "Preventivo"}</span>
        </div>
        <div className="text-xs text-slate-500 mt-1">Fornitore: <span className="text-slate-700">{item.fornitore || "—"}</span></div>
      </div>
    );
  }
  if (kind === "sinistri") {
    return (
      <div className="p-3 border border-slate-200 rounded-lg bg-white">
        <div className="flex flex-wrap items-baseline gap-2">
          <span className="text-xs font-mono text-slate-500">{item.pratica || "—"}</span>
          <span className="font-semibold text-slate-800">{item.descrizione || "Sinistro"}</span>
          {item.fase && <span className="ml-auto text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">{item.fase}</span>}
        </div>
      </div>
    );
  }
  // ras
  return (
    <div className="p-3 border border-slate-200 rounded-lg bg-white">
      <div className="flex flex-wrap items-baseline gap-2">
        <span className="font-semibold text-slate-800">{item.attivita || "Attività RAS"}</span>
        {item.scadenza && <span className="ml-auto text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-700">Scadenza {item.scadenza}</span>}
      </div>
      <div className="text-xs text-slate-500 mt-1">
        Fornitore incaricato: <span className="text-slate-700">{item.fornitore_incaricato || "—"}</span>
        {typeof item.giorni_alla_scadenza === "number" && (
          <span className={`ml-2 font-semibold ${item.giorni_alla_scadenza < 0 ? "text-red-600" : "text-amber-700"}`}>
            {item.giorni_alla_scadenza < 0 ? `Scaduta da ${Math.abs(item.giorni_alla_scadenza)}gg` : `Tra ${item.giorni_alla_scadenza}gg`}
          </span>
        )}
        {typeof item.solleciti_inviati === "number" && <span className="ml-2 text-slate-500">Solleciti: {item.solleciti_inviati}</span>}
      </div>
    </div>
  );
}


function Mini({ icon: Icon, label, value, color }) {
  return (
    <Card className="p-4 border-2 flex items-center gap-3">
      <div className={`rounded-lg p-2 ${color}`}><Icon className="w-6 h-6" /></div>
      <div><div className="text-3xl font-extrabold">{value}</div><div className="text-sm font-semibold text-slate-600">{label}</div></div>
    </Card>
  );
}

function Section({ title, rows, cols, empty, alert }) {
  return (
    <Card className={`p-5 border-2 mb-5 ${alert && rows.length ? "border-red-300" : ""}`}>
      <h3 className="font-display text-xl font-bold mb-3">{title} ({rows.length})</h3>
      {rows.length === 0 ? (
        <div className="text-slate-500 text-base">{empty}</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-base">
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} className="border-b border-slate-100">
                  {cols.map((c) => <td key={c} className="py-2 pr-4 align-top">{String(r[c] ?? "—")}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
